'use client';

import React, { ChangeEvent, useEffect, useId, useMemo, useState } from 'react';
import { Caption } from '../Caption/Caption';
import { AnimatePresence, motion } from 'motion/react';
import './InputColor.css';

export interface InputColorProps {
  label?: string;
  caption?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  message?: string;
  messageType?: '' | 'error' | 'success';
  id?: string;
  size?: 'sm' | 'md' | 'lg';
}

const HEX_REGEX = /^#([0-9A-F]{3}){1,2}$/i;

export const InputColor: React.FC<InputColorProps> = ({
  label,
  caption,
  value,
  defaultValue = '',
  onChange,
  required = false,
  disabled = false,
  readonly = false,
  placeholder = '#000000',
  message,
  messageType = '',
  id,
  size = 'md',
}) => {
  const [internal, setInternal] = useState<string>(value ?? defaultValue);
  const reactId = useId();
  const rootId = id ?? `input-color-${reactId}`;
  const captionId = caption ? `${rootId}-caption` : undefined;
  const messageId = message ? `${rootId}-message` : undefined;

  // Keep internal value in sync when controlled
  useEffect(() => {
    if (value !== undefined) {
      setInternal(value);
    }
  }, [value]);

  const isValidHex = useMemo(() => HEX_REGEX.test(internal), [internal]);

  const effectiveMessage = useMemo(() => {
    if (message) return message;
    if (internal && !isValidHex) return 'Invalid HEX code';
    return '';
  }, [internal, isValidHex, message]);

  const effectiveMessageType: '' | 'error' | 'success' = useMemo(() => {
    if (messageType) return messageType;
    if (effectiveMessage) return 'error';
    return '';
  }, [effectiveMessage, messageType]);

  const emitIfValid = (next: string) => {
    if (HEX_REGEX.test(next)) {
      onChange?.({ target: { value: next } } as unknown as ChangeEvent<HTMLInputElement>);
    }
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = String(e.target.value);
    setInternal(next);
    emitIfValid(next);
  };

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = String(e.target.value);
    setInternal(next);
    // Native color input always provides a valid 7-char hex
    onChange?.(e);
  };

  const colorSwatchValue = isValidHex ? internal : '#000000';

  return (
    <div className="input-color" data-disabled={disabled} data-readonly={readonly} data-size={size}>
      <label htmlFor={rootId}>
        {label && (
          <span className="input-color__label">
            {label}
            {required && <span className="input-color__required">*</span>}
          </span>
        )}
        {caption && (
          <Caption>
            <span id={captionId}>{caption}</span>
          </Caption>
        )}
        <motion.span
          className="input-color__wrapper"
          animate={{ x: effectiveMessageType === 'error' ? [0, -6, 6, -4, 4, 0] : 0 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          style={{ willChange: 'transform' }}
        >
          {/* Color swatch */}
          <input
            type="color"
            className="input-color__swatch"
            value={colorSwatchValue}
            onChange={handleColorChange}
            aria-label="Pick color"
            disabled={disabled || readonly}
          />
          {/* Text input */}
          {(() => (
            <input
              id={rootId}
              type="text"
              required={required}
              readOnly={readonly}
              disabled={disabled}
              placeholder={placeholder}
              value={internal}
              onChange={handleTextChange}
              autoComplete="off"
              aria-invalid={effectiveMessageType === 'error' || undefined}
              aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
            />
          ))()}
        </motion.span>
      </label>
      <AnimatePresence initial={false}>
        {effectiveMessage && (
          <motion.div
            key="input-color-message"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <span id={messageId} className="input-color__message" data-type={effectiveMessageType}>
              {effectiveMessage}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

InputColor.displayName = 'InputColor';
